import React, { Fragment } from 'react';
import {get} from 'lodash';
import className from 'classnames';
import moment from 'moment';
import axios from 'axios';
import pluralize from 'pluralize';

import {
  canEdit,
  canKenta,
} from '../lib/policies';

import {
  requestCallback,
} from '../lib/helpers';

import {useCheckIds, useModal} from '../hooks';

import ImageLoader from './ImageLoader';
import OverflowH from './OverflowH';
import DotTooltip from './DotTooltip';
import PenaltyTools from './PenaltyTools';
import Modal from './Modal';
import TableHeader from './TableHeader';
import BulkPenaltyTools from './BulkPenaltyTools';

const GroupMembersList = ({
  users = [],
  groupId,
  total,
}) => {
  const {
    checkIds,
    renderCheckBox, renderCheckBoxForCheckAll,
    renderOpenAllButton,
  } = useCheckIds({
    data: users,
    openUrl: 'users',
  })

  const [
    modal,
    modalSet,
    $submitButton,
  ] = useModal({
    keyboardBindings: true,
  })

  const [
    modalBulk,
    modalBulkSet,
    $submitButtonBulk,
  ] = useModal({
    keyboardBindings: true,
  })

  const onToggleModal = (user, action) => () => {
    modalSet(!action ? null : {
      action,
      user,
    })
  }

  const onToggleModalBulk = (action) => () => {
    modalBulkSet(!action ? null : action)
  }

  const handleRequest = () => {
    const baseUrl = `/${gon.currentProvider}/api/group_users/${get(modal, 'user.groupUserStatuses.id')}`
    const url = `${baseUrl}/${get(modal, 'action')}`

    requestCallback(axios.get(url, {
      params: {
        user_id: get(modal, 'user.id'),
        group_id: groupId,
      }
    }))
  }

  const handleRequestBulk = () => {
    requestCallback(axios.get(`/${gon.currentProvider}/api/bulk/group_users/${modalBulk}`, {
      params: {
        group_id: groupId,
        ids: checkIds,
      }
    }))
  }

  const bulkForceVerify = () => {
    requestCallback(axios.get(`/${gon.currentProvider}/api/bulk/users/require_id_check`, {
      params: {
        ids: checkIds,
      }
    }))
  }

  return (
    <Fragment>
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Group id {groupId}’s members</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} users</p>
        </div>

        {renderOpenAllButton()}
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}

        <div className='TableChecker__right'>
          {canEdit('User') &&
            <a
              onClick={bulkForceVerify}
              disabled={checkIds.length == 0}
              className='TableChecker__right__a TableChecker__right__a--flag'
              title='Force verify'
            />
          }

          {canEdit('Group') &&
            <Fragment>
              <a
                className='TableChecker__right__a TableChecker__right__a--person'
                title='Set admin'
                onClick={onToggleModalBulk('promote')}
                disabled={checkIds.length == 0}
              />

              <a
                className='TableChecker__right__a TableChecker__right__a--door'
                title='Kick'
                onClick={onToggleModalBulk('kick')}
                disabled={checkIds.length == 0}
              />
            </Fragment>
          }

          {canKenta('Group') &&
            <Fragment>
              <BulkPenaltyTools
                userIds={checkIds}
              />
            </Fragment>
          }
        </div>
      </div>

      <div className='Table Table--group-users-list'>
        {(() => {
          const columnHeaders = [
            {
              label: '',
            },
            {
              label: 'ID',
              sortKey: 'user_id',
            },
            {
              label: 'Nickname',
            },
            {
              label: 'Bio',
            },
            {
              label: 'Last login',
              sortKey: 'last_loggedin_at',
            },
            {
              label: 'Photo',
              sortKey: 'profile_icon'
            },
            {
              label: 'Status',
              sortKey: 'ban_until'
            },
            {
              label: 'Join Status'
            },
            {
              label: 'UUID',
            },
            {
              label: 'Reported<br />count',
              sortKey: 'reported_count'
            },
            {
              label: 'Manage',
            },
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <TableHeader
                  key={key}
                  header={header}
                />
              ))}
            </div>
          )
        })()}

        {users.map((user) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(user.id),
            })}
            key={user.id}
            data-x={user}
          >
            <div className='Table__col'>
              {renderCheckBox(user)}
            </div>

            <div className='Table__col'>
              <DotTooltip
                user={user}
              />
            </div>

            <div className='Table__col'>{user.nickname}</div>
            <div className='Table__col'>
              <OverflowH
                text={user.biography}
              />
            </div>

            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: user.lastLoggedinAt !== undefined ?
                    moment(user.lastLoggedinAt).format('YYYY-MM-DD<br />HH:mm')
                  :
                    'N/A'
                }
              }
            />

            <div className='Table__col'>
              <div className='ImageBorder'>
                <ImageLoader
                  url={get(user, 'profileIcon.thumb.url')}
                />
              </div>
            </div>

            <div className='Table__col'>
              {(() => {
                if (user.isBanned) {
                  return <span className='Badge Badge--orange'>Temp banned</span>
                } else if (user.type === 'deleted') {
                  return <span className='Badge Badge--red'>DELETED</span>
                } else {
                  return <span className='Badge'>Active</span>
                }
              })()}
            </div>

            <div className='Table__col'>
              {(() => {
                if (user.groupUserStatuses?.pending) {
                  return <span className='Badge Badge--yellow'>Pending</span>
                } else if (user.groupUserStatuses?.banned) {
                  return <span className='Badge Badge--red'>Banned</span>
                } else {
                  return <span className='Badge'>Joined</span>
                }
              })()}
            </div>

            <div className='Table__col'>{user.uuid}</div>

            <div className='Table__col'>{user.reportedCount}</div>

            <div className='Table__col'>
              <div className='Table__controls'>
                {canEdit('Group') &&
                  <Fragment>
                    <a
                      className='Table__controls__a Table__controls__a--person'
                      title='Set admin'
                      onClick={onToggleModal(user, 'promote')}
                    />
                    <a
                      className='Table__controls__a Table__controls__a--door'
                      title='Kick'
                      onClick={onToggleModal(user, 'kick')}
                    />
                  </Fragment>
                }

                {canKenta('Group') &&
                  <PenaltyTools
                    tableView
                    userId={user.id}
                    bannedTimeLeft={user.bannedTimeLeft}
                    type="Group"
                    banUntil={user.banUntil}
                    isBanned={user.isBanned}
                  />
                }
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal
        isActive={modal}
        onToggleModal={onToggleModal(null)}
      >
        <h3 className='Modal__h3'>
          {(() => {
            switch (get(modal, 'action')) {
              case 'promote': return `Promote user ${get(modal, 'user.id')} to admin`
              case 'demote': return `Demote user ${get(modal, 'user.id')} from admin`
              case 'kick': return `Kick user ${get(modal, 'user.id')} from group`
            }
          })()}
        </h3>

        <div className='Modal__p__wrapper'>
          <p className='Modal__p'>Are you sure you want to proceed?</p>
        </div>

        <div className='Modal__footer Modal__footer--without-border'>
          <a
            className='Button Button--gray-light'
            onClick={onToggleModal(null)}
          >
            Cancel
          </a>

          <button
            className='Button Button--blue'
            onClick={handleRequest}
            ref={$submitButton}
          >
            Submit
          </button>
        </div>
      </Modal>

      <Modal
        isActive={modalBulk}
        onToggleModal={onToggleModalBulk(null)}
      >
        <h3 className='Modal__h3'>
          {(() => {
            const text = `${checkIds.length} ${pluralize('user', checkIds.length,)}`

            switch (modalBulk) {
              case 'promote': return `Promote ${text} to admin`
              case 'demote': return `Demote ${text} from admin`
              case 'kick': return `Kick ${text} from group`
            }
          })()}
        </h3>

        <div className='Modal__p__wrapper'>
          <p className='Modal__p'>Are you sure you want to proceed?</p>
        </div>

        <div className='Modal__footer Modal__footer--without-border'>
          <a
            className='Button Button--gray-light'
            onClick={onToggleModalBulk(null)}
          >
            Cancel
          </a>

          <button
            className='Button Button--blue'
            onClick={handleRequestBulk}
            ref={$submitButtonBulk}
          >
            Submit
          </button>
        </div>
      </Modal>
    </Fragment>
  );
}

export default GroupMembersList;
